import { useState, useEffect } from 'react';
import { QUESTIONS } from '../data/questions';
import { COLORS, FONTS, TRACKING } from '../styles/tokens';
import Icon from './Icon';

const TOTAL = QUESTIONS.length;

export default function QuestionScreen({ currentQ, answers, onSelect, onBack }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [kbIdx, setKbIdx]             = useState(null); // keyboard-highlighted answer
  const [mouseIdx, setMouseIdx]       = useState(null); // mouse-hovered answer
  const q        = QUESTIONS[currentQ];
  const progress = Math.round((currentQ / TOTAL) * 100);

  // Reset all transient state when the question changes
  useEffect(() => {
    setSelectedIdx(null);
    setKbIdx(null);
    setMouseIdx(null);
  }, [currentQ]);

  // Keyboard handler
  useEffect(() => {
    function onKey(e) {
      // Don't intercept while an answer is already animating out
      if (selectedIdx !== null) return;

      const n = q.answers.length;

      // 1–n → instantly select that answer
      const num = parseInt(e.key, 10);
      if (!isNaN(num) && num >= 1 && num <= n) {
        e.preventDefault();
        const idx = num - 1;
        setSelectedIdx(idx);
        setTimeout(() => onSelect(q.id, q.answers[idx]), 280);
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setKbIdx(prev => (prev === null ? 0 : (prev + 1) % n));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setKbIdx(prev => (prev === null ? n - 1 : (prev - 1 + n) % n));
          break;
        case 'Enter':
        case ' ':
          if (kbIdx !== null) {
            e.preventDefault();
            setSelectedIdx(kbIdx);
            setTimeout(() => onSelect(q.id, q.answers[kbIdx]), 280);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onBack();
          break;
        default:
          break;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentQ, selectedIdx, kbIdx, q, onSelect, onBack]);

  function handleClick(answer, idx) {
    if (selectedIdx !== null) return;
    setSelectedIdx(idx);
    setTimeout(() => onSelect(q.id, answer), 280);
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bgApp, padding: '0 20px', boxSizing: 'border-box' }}>
      <main id="main-content" style={{ maxWidth: 680, margin: '0 auto', padding: '44px 0 80px' }}>

        {/* Screen-reader announcement when question changes */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {q.section}: Question {currentQ + 1} of {TOTAL}. {q.text}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 36 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: TRACKING.kicker,
              color: q.sectionColor,
              margin: 0,
            }}>
              <Icon name={q.sectionIcon} size={14} style={{ marginRight: 4 }} />{q.section}
            </p>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 12,
              color: COLORS.textMuted,
              margin: 0,
            }} aria-hidden="true">
              {currentQ + 1} of {TOTAL}
            </p>
          </div>
          <div
            role="progressbar"
            aria-valuenow={currentQ + 1}
            aria-valuemin={1}
            aria-valuemax={TOTAL}
            aria-label={`Question ${currentQ + 1} of ${TOTAL}`}
            style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}
          >
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: COLORS.holidayBlue,
              borderRadius: 2,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        {/* Question */}
        <h2 style={{
          fontFamily: FONTS.heading,
          fontSize: 'clamp(20px, 3vw, 27px)',
          fontWeight: 700,
          lineHeight: 1.25,
          color: COLORS.textPrimary,
          margin: '0 0 8px',
          letterSpacing: '-0.01em',
        }}>
          {q.text}
        </h2>

        {q.subtitle && (
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            fontStyle: 'italic',
            color: COLORS.textMuted,
            lineHeight: 1.5,
            margin: '0 0 28px',
          }}>
            {q.subtitle}
          </p>
        )}

        {!q.subtitle && <div style={{ height: 28 }} />}

        {/* Answers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.answers.map((answer, idx) => {
            const isSelected  = selectedIdx === idx;
            const isKbFocused = kbIdx === idx && selectedIdx === null;
            const isHovered   = mouseIdx === idx && selectedIdx === null;

            const bg = isSelected
              ? COLORS.blue
              : isKbFocused
                ? COLORS.surfaceHigh
                : isHovered
                  ? COLORS.surfaceHigh
                  : COLORS.surface;

            const borderColor = isSelected
              ? COLORS.blue
              : isKbFocused
                ? COLORS.holidayBlue
                : isHovered
                  ? 'rgba(42,192,208,0.5)'
                  : 'rgba(255,255,255,0.10)';

            const badgeBg = isSelected
              ? 'rgba(255,255,255,0.25)'
              : isKbFocused
                ? COLORS.holidayBlue
                : 'rgba(255,255,255,0.08)';

            const badgeColor = isSelected || isKbFocused
              ? COLORS.bgApp
              : COLORS.textMuted;

            return (
              <button
                key={idx}
                onClick={() => handleClick(answer, idx)}
                onMouseEnter={() => { if (selectedIdx === null) setMouseIdx(idx); }}
                onMouseLeave={() => setMouseIdx(null)}
                disabled={selectedIdx !== null}
                aria-pressed={isSelected}
                className="btn-answer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  width: '100%',
                  textAlign: 'left',
                  padding: '14px 18px',
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  lineHeight: 1.4,
                  fontWeight: isSelected ? 600 : 400,
                  background: bg,
                  color: isSelected ? COLORS.white : COLORS.textPrimary,
                  border: `1.5px solid ${borderColor}`,
                  borderRadius: 6,
                  cursor: selectedIdx !== null ? 'default' : 'pointer',
                  transition: 'background 0.12s ease, border-color 0.12s ease',
                  boxShadow: isKbFocused ? '0 0 0 2px rgba(42,192,208,0.2)' : 'none',
                }}
              >
                {/* Number badge */}
                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    background: badgeBg,
                    color: badgeColor,
                    fontSize: 11,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    transition: 'background 0.12s ease, color 0.12s ease',
                  }}
                >
                  {idx + 1}
                </span>

                {answer.text}
              </button>
            );
          })}
        </div>

        {/* Back */}
        <div style={{ marginTop: 16 }}>
          <button
            onClick={onBack}
            aria-label={currentQ === 0 ? 'Back to introduction' : `Back to question ${currentQ}`}
            style={{
              background: 'none',
              border: 'none',
              fontFamily: FONTS.body,
              fontSize: 14,
              color: COLORS.textMuted,
              cursor: 'pointer',
              padding: '8px 0',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            <Icon name="arrow_back" size={14} style={{ marginRight: 4 }} />Back
          </button>
        </div>

      </main>
    </div>
  );
}

